import React from "react";
import styled, { css, useTheme } from "styled-components";
import { variant } from "styled-system";
import { Icon } from "./Icon";
import { Box } from "./Box";
import { Text } from "./Text";
import { Flex } from "./Flex";
const getVariants = ({ borders, radii, fontSizes, colors: { badgeColors } }) => ({
    alert: {
        backgroundColor: badgeColors.alertBackground,
        border: borders[1],
        borderColor: badgeColors.alertBackground,
        color: badgeColors.alertText,
    },
    chip: {
        alignItems: "center",
        backgroundColor: badgeColors.chipBackground,
        border: borders[1],
        borderColor: badgeColors.chipBorder,
        borderRadius: radii[1],
        color: badgeColors.chipText,
        fontSize: fontSizes[1],
    },
    critical: {
        backgroundColor: badgeColors.criticalBackground,
        border: borders[1],
        borderColor: badgeColors.criticalBackground,
        color: badgeColors.criticalText,
    },
    criticalLight: {
        backgroundColor: badgeColors.criticalLightBackground,
        border: borders[1],
        borderColor: badgeColors.criticalLightBorder,
        color: badgeColors.criticalLightText,
    },
    custom: {
        backgroundColor: badgeColors.customBackground,
        border: borders[1],
        borderColor: badgeColors.customBackground,
        color: badgeColors.customText,
    },
    customLight: {
        backgroundColor: badgeColors.customLightBackground,
        border: borders[1],
        borderColor: badgeColors.customLightBorder,
        color: badgeColors.customLightText,
    },
    debug: {
        backgroundColor: badgeColors.debugBackground,
        border: borders[1],
        borderColor: badgeColors.debugBackground,
        color: badgeColors.debugText,
    },
    default: {
        backgroundColor: badgeColors.defaultBackground,
        border: borders[1],
        borderColor: badgeColors.defaultBackground,
        color: badgeColors.defaultText,
    },
    dry: {
        backgroundColor: badgeColors.dryBackground,
        border: borders[1],
        borderColor: badgeColors.dryBackground,
        color: badgeColors.dryText,
    },
    emergency: {
        backgroundColor: badgeColors.emergencyBackground,
        border: borders[1],
        borderColor: badgeColors.emergencyBackground,
        color: badgeColors.emergencyText,
    },
    emergencyLight: {
        backgroundColor: badgeColors.emergencyLightBackground,
        border: borders[1],
        borderColor: badgeColors.emergencyLightBorder,
        color: badgeColors.emergencyLightText,
    },
    error: {
        backgroundColor: badgeColors.errorBackground,
        border: borders[1],
        borderColor: badgeColors.errorBackground,
        color: badgeColors.errorText,
    },
    informational: {
        backgroundColor: badgeColors.informationalBackground,
        border: borders[1],
        borderColor: badgeColors.informationalBackground,
        color: badgeColors.informationalText,
    },
    informationalLight: {
        backgroundColor: badgeColors.informationalLightBackground,
        border: borders[1],
        borderColor: badgeColors.informationalLightBorder,
        color: badgeColors.informationalLightText,
    },
    notice: {
        backgroundColor: badgeColors.noticeBackground,
        border: borders[1],
        borderColor: badgeColors.noticeBackground,
        color: badgeColors.noticeText,
    },
    primary: {
        backgroundColor: badgeColors.primaryBackground,
        border: borders[1],
        borderColor: badgeColors.primaryBackground,
        color: badgeColors.primaryText,
    },
    primaryDark: {
        backgroundColor: badgeColors.primaryDarkBackground,
        border: borders[1],
        borderColor: badgeColors.primaryDarkBackground,
        color: badgeColors.primaryDarkText,
    },
    primaryLight: {
        backgroundColor: badgeColors.primaryLightBackground,
        border: borders[1],
        borderColor: badgeColors.primaryLightBorder,
        color: badgeColors.primaryLightText,
    },
    secondary: {
        backgroundColor: badgeColors.secondaryBackground,
        border: borders[1],
        borderColor: badgeColors.secondaryBackground,
        color: badgeColors.secondaryText,
    },
    secondaryLight: {
        backgroundColor: badgeColors.secondaryLightBackground,
        border: borders[1],
        borderColor: badgeColors.secondaryLightBorder,
        color: badgeColors.secondaryLightText,
    },
    success: {
        backgroundColor: badgeColors.successBackground,
        border: borders[1],
        borderColor: badgeColors.successBackground,
        color: badgeColors.successText,
    },
    successLight: {
        backgroundColor: badgeColors.successLightBackground,
        border: borders[1],
        borderColor: badgeColors.successLightBorder,
        color: badgeColors.successLightText,
    },
    warning: {
        backgroundColor: badgeColors.warningBackground,
        border: borders[1],
        borderColor: badgeColors.warningBackground,
        color: badgeColors.warningText,
    },
    warningLight: {
        backgroundColor: badgeColors.warningLightBackground,
        border: borders[1],
        borderColor: badgeColors.warningLightBorder,
        color: badgeColors.warningLightText,
    },
});
const sizes = {
    condensed: {
        borderRadius: "4px",
        fontSize: "10px",
        height: "16px",
        padding: "1px 6px",
    },
    normal: {
        borderRadius: 1,
        height: "20px",
        padding: "2px 8px",
    },
};
const DEFAULT_VARIANT = "primary";
const StyledBadge = styled(Flex) `
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  margin-right: 4px;
  white-space: nowrap;
  ${variant({ prop: "size", variants: sizes })}
  ${({ theme, variant = DEFAULT_VARIANT }) => (Object.assign({}, getVariants(theme)[variant]))}
  ${({ onClick }) => onClick &&
    css `
      &:hover {
        cursor: pointer;
      }
    `}
    ${({ disabled }) => disabled &&
    css `
      cursor: not-allowed;
      opacity: 0.7;
      pointer-events: none;
    `}
`;
export const Badge = ({ size = "normal", variant = DEFAULT_VARIANT, onCloseClick, label = "", text = "", onClick, disabled, addTestId = true, children, props, }) => {
    const { colors: { badgeColors }, } = useTheme();
    return (React.createElement(StyledBadge, Object.assign({ variant: variant, size: size, onClick: onClick, disabled: disabled }, props, { "data-testid": addTestId ? "badgeTestId" : "" }),
        onCloseClick ? (React.createElement(Box, { color: badgeColors.closeButton, cursor: "pointer", "data-testid": "closeBadge", onClick: onCloseClick, sx: { lineHeight: "0px", paddingRight: 1 } },
            React.createElement(Icon, { src: "Close", size: "12px" }))) : null,
        label && (React.createElement(Text, { color: badgeColors.badgeLabelText, fontWeight: "normal", mr: 1, whiteSpace: "nowrap", "font-size": "11px" },
            label,
            ":",
            " ")),
        text || children));
};
